import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, Loader2, RefreshCw } from 'lucide-react';
import { useFristApprovalOptional } from './FristApprovalContext';
import { listPortalFristenForAkte } from './portalFristen';
import type { PortalFristRecord } from './fristApprovalTypes';

const MIN_RELOAD_INTERVAL_MS = 30_000;

function formatDeDate(iso: string): string {
  const d = iso.trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(d)) return d;
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day}.${m}.${y}`;
}

function normalizeAkteKey(path?: string | null): string {
  return String(path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/\/$/, '')
    .toLowerCase();
}

function buildResolvedCardsSignature(
  cards: Record<string, { status: string; resolvedAt?: string }> | undefined,
): string {
  if (!cards) return '';
  return Object.entries(cards)
    .filter(([, c]) => c.status === 'approved' || c.status === 'revoked')
    .map(([id, c]) => `${id}:${c.status}:${c.resolvedAt ?? ''}`)
    .sort()
    .join('|');
}

export interface PortalFristenAktePanelProps {
  customerId: string;
  authToken?: string | null;
  akteNcPath?: string | null;
  className?: string;
  /** `strip` = ohne äußeren Rahmen im Collapsible-Strip; `hidden` = nur Daten/Summary. */
  embedMode?: 'standalone' | 'strip' | 'hidden';
  onCollapsedSummaryChange?: (summary: string | null) => void;
}

/**
 * Aktive Fristen der geöffneten Akte (PB `portal_fristen` oder Demo-Mock).
 */
function buildFristenCollapsedSummary(
  items: PortalFristRecord[],
  loading: boolean,
  loadedOnce: boolean,
): string | null {
  if (!loadedOnce && loading) return null;
  if (!items.length) {
    return loadedOnce ? 'Keine aktiven Fristen' : null;
  }
  const first = items[0];
  const tail = items.length > 1 ? ` · +${items.length - 1}` : '';
  return `${items.length} Frist${items.length !== 1 ? 'en' : ''} · ${first.frist_typ}${tail}`;
}

export const PortalFristenAktePanel: React.FC<PortalFristenAktePanelProps> = ({
  customerId,
  authToken,
  akteNcPath,
  className = '',
  embedMode = 'standalone',
  onCollapsedSummaryChange,
}) => {
  const ctx = useFristApprovalOptional();
  const [items, setItems] = useState<PortalFristRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const loadedOnceRef = useRef(false);
  const lastReloadAtRef = useRef(0);
  const resolvedSignatureRef = useRef('');
  const akteNcPathRef = useRef(akteNcPath);
  const authTokenRef = useRef(authToken);
  akteNcPathRef.current = akteNcPath;
  authTokenRef.current = authToken;

  const akteKey = useMemo(() => normalizeAkteKey(akteNcPath), [akteNcPath]);

  const resolvedSignature = useMemo(
    () => buildResolvedCardsSignature(ctx?.cards),
    [ctx?.cards],
  );

  const reload = useCallback(
    async (options?: { silent?: boolean; force?: boolean }) => {
      if (!customerId?.trim()) return;

      const now = Date.now();
      if (!options?.force && now - lastReloadAtRef.current < MIN_RELOAD_INTERVAL_MS) return;
      lastReloadAtRef.current = now;

      const showSpinner = !options?.silent && !loadedOnceRef.current;
      if (showSpinner) setLoading(true);
      try {
        const rows = await listPortalFristenForAkte(
          customerId,
          authTokenRef.current?.trim() ?? '',
          akteNcPathRef.current?.trim() || undefined,
        );
        setItems(rows);
        loadedOnceRef.current = true;
        setLoadedOnce(true);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [customerId],
  );

  useEffect(() => {
    lastReloadAtRef.current = 0;
    loadedOnceRef.current = false;
    setLoadedOnce(false);
    void reload({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akteKey, customerId]);

  useEffect(() => {
    if (!resolvedSignature) return;
    if (resolvedSignatureRef.current === resolvedSignature) return;
    resolvedSignatureRef.current = resolvedSignature;
    lastReloadAtRef.current = 0;
    void reload({ silent: true, force: true });
  }, [resolvedSignature, reload]);

  const collapsedSummary = buildFristenCollapsedSummary(items, loading, loadedOnce);

  useEffect(() => {
    onCollapsedSummaryChange?.(collapsedSummary);
  }, [collapsedSummary, onCollapsedSummaryChange]);

  if (embedMode === 'hidden') return null;

  if (!akteNcPath?.trim() && items.length === 0 && !loadedOnce) return null;

  const shellClass =
    embedMode === 'strip'
      ? `px-3 py-2 min-h-[3.25rem] ${className}`
      : `rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 min-h-[3.25rem] ${className}`;

  return (
    <div
      className={shellClass}
      aria-label="Aktive Fristen in dieser Akte"
    >
      {embedMode !== 'strip' ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0 flex items-center gap-1">
            <CalendarClock className="w-3 h-3" aria-hidden />
            Fristen in dieser Akte
          </p>
          <button
            type="button"
            onClick={() => {
              lastReloadAtRef.current = 0;
              void reload({ force: true });
            }}
            disabled={loading}
            className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-50"
            title="Fristen neu laden"
            aria-label="Fristen neu laden"
          >
            {loading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
          </button>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              lastReloadAtRef.current = 0;
              void reload({ force: true });
            }}
            disabled={loading}
            className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-50"
            title="Fristen neu laden"
            aria-label="Fristen neu laden"
          >
            {loading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-[11px] text-slate-500 m-0 mt-1.5">
          {loading
            ? 'Lade…'
            : 'Keine aktiven Fristen gespeichert. Fristen erscheinen nach Bestätigung im ARKO-Chat.'}
        </p>
      ) : (
        <ul className="m-0 mt-1.5 p-0 list-none space-y-1.5">
          {items.map((f) => (
            <li
              key={f.id}
              className="text-[11px] text-slate-300 border-t border-white/[0.04] pt-1.5 first:border-0 first:pt-0"
            >
              <span className="font-medium text-slate-200">{f.frist_typ}</span>
              <span className="text-slate-500">
                {' '}
                · {formatDeDate(f.start_datum)} → {formatDeDate(f.ende_datum)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
